import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    ElementRef,
    HostListener,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import {SafeUrl} from '@angular/platform-browser';
import {Params} from '@angular/router';
import {CmsComponentData} from '@spartacus/storefront';
import {Observable} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {AimoCMSBannerComponent} from '../../../model/cms.model';
import {GTMContentType, GTMCreativeSlot, GTMEventCode} from '../../../service/gtm/aimo-gtm.model';
import {AimoGtmService} from '../../../service/gtm/aimo-gtm.service';
import {SafeUrlPipe} from '../utils/safe-html/aimo-pipes';


@Component({
    selector: 'aimo-banner',
    templateUrl: './aimo-banner.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AimoBannerComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() banner: AimoCMSBannerComponent;
    gtmScrollActive = true;
    componentData$: Observable<AimoCMSBannerComponent>;
    @ViewChild('bannerWrapper', {static: false})
    private bannerWrapper: ElementRef<HTMLDivElement>;
    private mainBanner: AimoCMSBannerComponent;

    constructor(
        protected component: CmsComponentData<AimoCMSBannerComponent>,
        protected gtmService: AimoGtmService,
        public safeUrlPipe: SafeUrlPipe,
        @Inject(DOCUMENT) protected document: Document,
    ) {
    }

    ngOnInit(): void {
        this.banner = this.banner !== undefined ? {...this.banner} : undefined;
        if (this.banner !== undefined && !this.isExternal(this.banner) && this.banner?.urlLink) {
            this.banner = this.setBannerParams(this.banner);
            this.tryToTriggerGtmEvent(this.banner);
        }
        this.componentData$ = this.component.data$.pipe(
            filter(Boolean),
            map((banner) => {
                    this.mainBanner = this.setBannerParams(banner);
                    return this.mainBanner;
                }
            )
        );
    }

    private setBannerParams(banner: AimoCMSBannerComponent): AimoCMSBannerComponent {
        return {
            ...banner,
            urlLink: banner.urlLink?.replace(/\?.*/, ''),
            urlLinkFragment: banner.urlLink?.includes('#') ? banner.urlLink?.split('#')[1] : null,
            urlParams: this.getInternalQueryParams(banner)
        };
    }

    ngAfterViewInit(): void {
        this.isScrolledIntoView();
    }

    ngOnDestroy(): void {
    }

    @HostListener('window:scroll', [])
    isScrolledIntoView() {
        this.tryToTriggerGtmEvent(this.banner ?? this.mainBanner);
    }

    tryToTriggerGtmEvent(banner: AimoCMSBannerComponent): void {
        if (banner && this.gtmScrollActive) {
            if (AimoGtmService.triggerInViewEvent(this.document, this.bannerWrapper, true, () =>
                this.gtmEvent(banner, GTMEventCode.ViewPromotion),
            )) {
                this.gtmScrollActive = false;
            }
        }
    }

    buttonClicked(banner: AimoCMSBannerComponent): void {
        this.gtmEvent(banner, GTMEventCode.SelectPromotion);
    }

    private gtmEvent(banner: AimoCMSBannerComponent, event: GTMEventCode): void {
        this.gtmService.pushContentEvent(
            this.defineCreativeSlot(banner),
            banner.name,
            GTMContentType.aimo,
            banner.gtmCustomer?.code,
            event,
        );
    }

    private defineCreativeSlot(banner: AimoCMSBannerComponent): GTMCreativeSlot {
        if (banner.bannerType === 'HERO') {
            return GTMCreativeSlot.front_page_hero_banner;
        } else if (banner.bannerType === 'LARGE') {
            return GTMCreativeSlot.front_page_large_banner;
        }
        return GTMCreativeSlot.category_page_vendor_promotion;
    }

    isExternal(data: AimoCMSBannerComponent): boolean | null {
        return data?.external === 'true' || data?.external === true;
    }

    getTarget(data: AimoCMSBannerComponent): string | null {
        return this.isExternal(data) ? '_blank' : null;
    }

    getSafeUrl(banner: AimoCMSBannerComponent): SafeUrl {
        return this.safeUrlPipe.transform(banner.urlLink + '?' + new URLSearchParams(banner.urlParams as any).toString());
    }

    private getInternalQueryParams(banner: AimoCMSBannerComponent): Params {
        const params: Params = banner.urlLink?.includes('?') ? banner.urlLink?.split('?')[1].replace(/\#.*/, '')
            .split('&')
            .map(param => param.split('='))
            .map(([key, value]) => ({[key]: value}))
            .reduce((acc, curr) => ({...acc, ...curr}), {}) : {};
        if (banner.external === 'true') {
            return params;
        }

        const ret = {
            ...params,
            promotion_id: banner.uid,
            promotion_name: banner.name,
            creative_slot: this.defineCreativeSlot(banner)
        };
        return ret;
    }

    protected getBgClass(banner: AimoCMSBannerComponent) {
        return 'bg-' + banner?.bannerColor?.toLowerCase().replace('_', '-');
    }
}
